import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import './ServiceProgressBar.scss';

// Shown while the service bar is pinned, which the bar works out and passes down
const ServiceProgressBar = ({ songs, continuousView, pinned: isServiceBarPinned }) => {
  const { t } = useTranslation();
  const [activeSegments, setActiveSegments] = useState(new Set([0]));
  const [segmentProgress, setSegmentProgress] = useState({});

  useEffect(() => {
    if (!songs || songs.length === 0) {
      return;
    }

    // Request Animation Frame ID for throttling
    let rafId = null;

    const updateProgress = () => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        // In continuous view, track all song sections
        // In single song view, track just the current song (always index 0 for display)
        const songSections = continuousView
          ? document.querySelectorAll('.continuous-view__song-section')
          : [document.getElementById('view-body')];

        if (!songSections || songSections.length === 0 || !songSections[0]) {
          rafId = null;
          return;
        }

        const viewportHeight = window.innerHeight;
        const headerOffset = 120; // Account for sticky header
        const newActiveSegments = new Set();
        const newSegmentProgress = {};

        // Get the maximum scrollable distance
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        const currentScroll = window.pageYOffset;

        // Calculate progress for each segment
        songSections.forEach((section, index) => {
          const rect = section.getBoundingClientRect();
          const sectionTop = rect.top;
          const sectionHeight = rect.height;
          const sectionBottom = sectionTop + sectionHeight;

          // Get absolute position from top of document
          const absoluteSectionTop = sectionTop + currentScroll;

          // Calculate how much of the section has been scrolled through
          let progress = 0;

          // If section hasn't reached the header yet
          if (sectionTop >= headerOffset) {
            progress = 0;
          }
          // If section has been completely scrolled past (bottom is above header)
          else if (sectionBottom <= headerOffset) {
            progress = 100;
          }
          // Section is currently being scrolled through
          else {
            const isLastSection = index === songSections.length - 1;

            // For the last song in continuous view or single song view,
            // calculate based on remaining scrollable space
            if (isLastSection || !continuousView) {
              const scrollableRangeStart = absoluteSectionTop - headerOffset;
              const scrollableRangeEnd = maxScroll;
              const scrollableRange = scrollableRangeEnd - scrollableRangeStart;

              if (scrollableRange > 0) {
                const scrolledIntoSection = Math.max(0, currentScroll - scrollableRangeStart);
                progress = Math.min(100, (scrolledIntoSection / scrollableRange) * 100);
              } else {
                // If there's no scrollable range, it's complete
                progress = 100;
              }
            } else {
              // For intermediate songs in continuous view, calculate progress
              // based on how much has passed above the header
              const scrolledAmount = headerOffset - sectionTop;
              progress = Math.min(100, Math.max(0, (scrolledAmount / sectionHeight) * 100));
            }
          }

          newSegmentProgress[index] = progress;

          // Check if section is visible in viewport (with header offset)
          const visibleTop = Math.max(rect.top, headerOffset);
          const visibleBottom = Math.min(rect.bottom, viewportHeight);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);

          // Mark segment as active if section has any visibility
          if (visibleHeight > 0) {
            newActiveSegments.add(index);
          }
        });

        setActiveSegments(newActiveSegments);
        setSegmentProgress(newSegmentProgress);
        rafId = null;
      });
    };

    // Initial check
    updateProgress();

    // Update on scroll with RAF throttling
    const handleScroll = () => {
      updateProgress();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [continuousView, songs]);

  // Don't render if no songs or service bar is not pinned
  if (!songs || songs.length === 0 || !isServiceBarPinned) {
    return null;
  }

  // In single song view, we only display one segment for the current song
  const displaySongs = continuousView ? songs : [songs[0]];
  const segmentWidth = 100 / displaySongs.length;

  // Handle clicking on a segment to jump to that song
  const handleSegmentClick = (index) => {
    if (!continuousView) {
      // In single song view, scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      return;
    }

    const songSections = document.querySelectorAll('.continuous-view__song-section');
    const targetSection = songSections[index];

    if (targetSection) {
      const headerOffset = 100;
      const elementPosition = targetSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className={`service-progress-bar ${isServiceBarPinned ? 'visible' : ''}`}>
      <div className="service-progress-bar__track">
        {displaySongs.map((song, index) => {
          const isActive = activeSegments.has(index);
          const progress = segmentProgress[index] || 0;
          const isCompleted = progress >= 100;
          const songRemainingProgress = 100 - progress;

          return (
            <div
              key={song._id || index}
              className={`service-progress-bar__segment ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              style={{ width: `${segmentWidth}%` }}
              onClick={() => handleSegmentClick(index)}
              title={
                continuousView
                  ? t('view.jumpToSong', { number: index + 1, title: song.title })
                  : t('view.scrollToTop', { title: song.title })
              }
            >
              {/* Only show remaining progress indicator in continuous view */}
              {continuousView && !isCompleted && progress > 0 && (
                <div
                  className="service-progress-bar__segment-remaining"
                  style={{ width: `${songRemainingProgress}%` }}
                />
              )}
              <div className="service-progress-bar__segment-fill" style={{ width: `${progress}%` }} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

ServiceProgressBar.propTypes = {
  songs: PropTypes.array,
  continuousView: PropTypes.bool,
  pinned: PropTypes.bool,
};

export default ServiceProgressBar;
